import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { FileText, Loader2, CheckCircle, XCircle, Clock, Trash2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';

interface WLLSFilesListProps {
  onFileSelected: (fileId: string) => void;
  onFileDeleted?: (fileId: string) => void;
  refresh: number;
}

export const WLLSFilesList: React.FC<WLLSFilesListProps> = ({ onFileSelected, onFileDeleted, refresh }) => {
  const queryClient = useQueryClient();
  const { data: files, isLoading } = useQuery({
    queryKey: ['wlls-files', refresh],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('wlls_files')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data;
    }
  });

  const deleteFileMutation = useMutation({
    mutationFn: async (fileId: string) => {
      const { error } = await supabase
        .from('wlls_files')
        .delete()
        .eq('id', fileId);
      
      if (error) throw error;
    },
    onSuccess: (_, fileId) => {
      queryClient.invalidateQueries({ queryKey: ['wlls-files'] });
      onFileDeleted?.(fileId);
      toast.success('File and patterns deleted successfully');
    },
    onError: (error) => {
      console.error('Delete error:', error);
      toast.error('Failed to delete file');
    }
  });

  const handleDelete = (fileId: string, filename: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent file selection
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'error':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'processing':
        return <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />;
      default:
        return <Clock className="h-4 w-4 text-yellow-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'error':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'processing':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">Loading files...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          WLLS Files
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!files || files.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            No WLLS files uploaded yet
          </p>
        ) : (
          <div className="space-y-2">
            {files.map((file) => (
              <div
                key={file.id}
                className="p-3 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                onClick={() => onFileSelected(file.id)}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-sm truncate">{file.filename}</span>
                  <div className="flex items-center gap-2">
                    {getStatusIcon(file.analysis_status)}
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={(e) => handleDelete(file.id, file.filename, e)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete WLLS File</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete "{file.filename}"? This will permanently remove the file and all its associated patterns. This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => deleteFileMutation.mutate(file.id)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            disabled={deleteFileMutation.isPending}
                          >
                            {deleteFileMutation.isPending ? 'Deleting...' : 'Delete'}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
                
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>{formatDistanceToNow(new Date(file.created_at), { addSuffix: true })}</span>
                  <Badge 
                    variant="outline" 
                    className={getStatusColor(file.analysis_status)}
                  >
                    {file.analysis_status}
                  </Badge>
                </div>
                
                {file.total_trades && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {file.total_trades} trades analyzed
                  </p>
                )}
                
                {file.error_message && (
                  <p className="text-xs text-red-500 mt-1 truncate">
                    {file.error_message}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};